import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { 
  Lightbulb, ArrowLeft, Plus, Bot, Sparkles, ThumbsUp,
  ArrowRight, CheckCircle, Clock, TrendingUp, Users, FileText,
  Loader2, MessageSquare, Star, Filter, ChevronRight
} from "lucide-react";
import HermesChat from "@/components/HermesChat";


import TopNav from "@/components/TopNav";
import HubSwitcher from "@/components/HubSwitcher";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { id: "submitted", label: "Submitted", color: "blue", icon: Lightbulb },
  { id: "triage", label: "Triage", color: "amber", icon: Filter },
  { id: "analysis", label: "Analysis", color: "purple", icon: TrendingUp },
  { id: "development", label: "Development", color: "emerald", icon: ArrowRight },
  { id: "completed", label: "Completed", color: "green", icon: CheckCircle },
];

export default function InnovationCentre() {
  const [ideas, setIdeas] = useState([]);
  const [selectedIdea, setSelectedIdea] = useState(null);
  const [stats, setStats] = useState(null);
  const [stageFilter, setStageFilter] = useState("");
  
  const [isSubmitOpen, setIsSubmitOpen] = useState(false);
  const [newIdea, setNewIdea] = useState({
    title: "", description: "", problem_statement: "", proposed_solution: "", expected_impact: "", category: "general"
  });
  
  
  const [isEnriching, setIsEnriching] = useState(false);
  const [isEvaluating, setIsEvaluating] = useState(false);

  const getHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ideasRes, statsRes] = await Promise.all([
        axios.get(`${API}/innovation/ideas`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/innovation/stats`, { headers: getHeaders(), withCredentials: true }),
      ]);
      setIdeas(ideasRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast.error("Failed to load innovation data");
    }
  };

  const submitIdea = async () => {
    if (!newIdea.title.trim()) {
      toast.error("Please provide an idea title");
      return;
    }
    
    try {
      await axios.post(`${API}/innovation/ideas`, newIdea, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true
      });
      toast.success("Idea submitted successfully!");
      setIsSubmitOpen(false);
      setNewIdea({ title: "", description: "", problem_statement: "", proposed_solution: "", expected_impact: "", category: "general" });
      fetchData();
    } catch (err) {
      toast.error("Failed to submit idea");
    }
  };

  const voteForIdea = async (ideaId) => {
    try {
      await axios.post(`${API}/innovation/ideas/${ideaId}/vote`, {}, {
        headers: getHeaders(),
        withCredentials: true
      });
      toast.success("Vote recorded!");
      fetchData();
    } catch (err) {
      toast.error("Failed to vote");
    }
  };

  const enrichIdea = async (ideaId) => {
    setIsEnriching(true);
    try {
      const res = await axios.post(`${API}/innovation/ideas/${ideaId}/enrich`, {}, {
        headers: getHeaders(),
        withCredentials: true
      });
      toast.success("Idea enriched with AI insights!");
      fetchData();
      if (selectedIdea?.id === ideaId) {
        setSelectedIdea({ ...selectedIdea, ai_enrichment: res.data.enrichment, stage: "triage" });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Enrichment failed");
    }
    setIsEnriching(false);
  };

  const evaluateIdea = async (ideaId) => {
    setIsEvaluating(true);
    try {
      const res = await axios.post(`${API}/innovation/ideas/${ideaId}/evaluate`, {}, {
        headers: getHeaders(),
        withCredentials: true
      });
      toast.success("Idea evaluated!");
      fetchData();
      if (selectedIdea?.id === ideaId) {
        setSelectedIdea({ ...selectedIdea, ai_evaluation: res.data.evaluation, priority: res.data.priority, stage: "analysis" });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Evaluation failed");
    }
    setIsEvaluating(false);
  };


  const filteredIdeas = stageFilter 
    ? ideas.filter(i => i.stage === stageFilter)
    : ideas;

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "critical": return "badge-red";
      case "high": return "badge-amber";
      case "medium": return "badge-blue";
      default: return "badge-green";
    }
  };

  const getStageColor = (stage) => {
    const s = STAGES.find(st => st.id === stage);
    return s ? `badge-${s.color}` : "badge-blue";
  };

  return (
    <div className="min-h-screen bg-app">
      <TopNav />
      {/* Header */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-slate-500 hover:text-slate-600">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500 flex items-center justify-center">
                <Lightbulb className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="font-['Barlow_Condensed'] text-xl font-semibold tracking-tight text-slate-800">
                  INNOVATION CENTRE
                </span>
                <p className="text-xs text-slate-600">Ideas • Enrichment • Evaluation</p>
              </div>
            </div>
          </div>
          
          <Dialog open={isSubmitOpen} onOpenChange={setIsSubmitOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary rounded-none text-white" data-testid="submit-idea-btn">
                <Plus className="w-4 h-4 mr-2" />
                Submit Idea
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-slate-200 rounded-none max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-['Barlow_Condensed']">Submit Your Innovation</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto pr-2">
                <div>
                  <Label className="text-slate-600">Idea Title *</Label>
                  <Input
                    placeholder="Give your idea a catchy title"
                    value={newIdea.title}
                    onChange={(e) => setNewIdea({ ...newIdea, title: e.target.value })}
                    className="bg-white border-slate-300 rounded-none"
                  />
                </div>
                <div>
                  <Label className="text-slate-600">Problem Statement</Label>
                  <Textarea
                    placeholder="What problem does this solve?"
                    value={newIdea.problem_statement}
                    onChange={(e) => setNewIdea({ ...newIdea, problem_statement: e.target.value })}
                    className="bg-white border-slate-300 rounded-none"
                  />
                </div>
                <div>
                  <Label className="text-slate-600">Proposed Solution</Label>
                  <Textarea
                    placeholder="How would you solve it?"
                    value={newIdea.proposed_solution}
                    onChange={(e) => setNewIdea({ ...newIdea, proposed_solution: e.target.value })}
                    className="bg-white border-slate-300 rounded-none"
                  />
                </div>
                <div>
                  <Label className="text-slate-600">Expected Impact</Label>
                  <Textarea
                    placeholder="What impact would this have?"
                    value={newIdea.expected_impact}
                    onChange={(e) => setNewIdea({ ...newIdea, expected_impact: e.target.value })}
                    className="bg-white border-slate-300 rounded-none"
                  />
                </div>
                <div>
                  <Label className="text-slate-600">Description</Label>
                  <Textarea
                    placeholder="Any additional details..."
                    value={newIdea.description}
                    onChange={(e) => setNewIdea({ ...newIdea, description: e.target.value })}
                    className="bg-white border-slate-300 rounded-none"
                  />
                </div>
                <Button className="btn-primary rounded-none w-full text-white" onClick={submitIdea}>
                  <Lightbulb className="w-4 h-4 mr-2" />
                  Submit Idea
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 flex gap-4">
        <div className="flex-1 min-w-0">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-5 gap-4 mb-6">
            {STAGES.map(stage => {
              const Icon = stage.icon;
              return (
                <div 
                  key={stage.id}
                  onClick={() => setStageFilter(stageFilter === stage.id ? "" : stage.id)}
                  className={`bg-white border p-4 cursor-pointer transition-all ${
                    stageFilter === stage.id ? "border-amber-500 shadow-md" : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <Icon className={`w-6 h-6 mb-2 text-${stage.color}-600`} />
                  <p className="text-2xl font-bold text-slate-800">{stats.by_stage[stage.id] || 0}</p>
                  <p className="text-xs text-slate-600">{stage.label}</p>
                </div>
              );
            })}
          </div>
        )}

        <Tabs defaultValue="ideas" className="w-full">
          <TabsList className="bg-slate-100 border border-slate-200 rounded-none p-1 mb-6">
            <TabsTrigger value="ideas" className="rounded-none data-[state=active]:bg-white">
              <Lightbulb className="w-4 h-4 mr-2" />
              Ideas Pipeline
            </TabsTrigger>
            <TabsTrigger value="bot" className="rounded-none data-[state=active]:bg-white">
              <Bot className="w-4 h-4 mr-2" />
              Innovation Bot
            </TabsTrigger>
          </TabsList>

          {/* Ideas Pipeline Tab */}
          <TabsContent value="ideas" className="space-y-6">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Ideas List */}
              <div className="lg:col-span-2 space-y-3">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-['Barlow_Condensed'] text-xl font-semibold text-slate-800">
                    {stageFilter ? STAGES.find(s => s.id === stageFilter)?.label : "All Ideas"} ({filteredIdeas.length})
                  </h2>
                  {stageFilter && (
                    <Button variant="outline" size="sm" className="border-slate-300 rounded-none" onClick={() => setStageFilter("")}>
                      Clear Filter
                    </Button>
                  )}
                </div>

                {filteredIdeas.length > 0 ? (
                  filteredIdeas.map((idea) => (
                    <div
                      key={idea.id}
                      onClick={() => setSelectedIdea(idea)}
                      className={`bg-white border p-4 cursor-pointer transition-all ${
                        selectedIdea?.id === idea.id ? "border-amber-500 shadow-md" : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold text-slate-800">{idea.title}</h3>
                          <p className="text-sm text-slate-600 mt-1 line-clamp-2">{idea.problem_statement || idea.description}</p>
                          <div className="flex items-center gap-2 mt-3">
                            <span className={`badge ${getStageColor(idea.stage)}`}>{idea.stage}</span>
                            <span className={`badge ${getPriorityColor(idea.priority)}`}>{idea.priority}</span>
                            <span className="text-xs text-slate-600">by {idea.submitter_name}</span>
                          </div>
                        </div>
                        <div className="flex flex-col items-center gap-1 ml-4">
                          <button 
                            onClick={(e) => { e.stopPropagation(); voteForIdea(idea.id); }}
                            className="text-slate-500 hover:text-amber-500"
                          >
                            <ThumbsUp className="w-5 h-5" />
                          </button>
                          <span className="text-sm font-semibold text-slate-700">{idea.votes}</span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12 bg-white border border-slate-200">
                    <Lightbulb className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-600">No ideas yet. Be the first to submit!</p>
                  </div>
                )}
              </div>

              {/* Idea Detail Panel */}
              <div className="bg-white border border-slate-200 p-6">
                {selectedIdea ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-['Barlow_Condensed'] text-xl font-semibold text-slate-800">
                        {selectedIdea.title}
                      </h3>
                      <span className={`badge ${getStageColor(selectedIdea.stage)}`}>{selectedIdea.stage}</span>
                    </div>

                    <div className="space-y-3">
                      {selectedIdea.problem_statement && (
                        <div>
                          <p className="text-xs text-slate-600 uppercase tracking-wider">Problem</p>
                          <p className="text-sm text-slate-700">{selectedIdea.problem_statement}</p>
                        </div>
                      )}
                      {selectedIdea.proposed_solution && (
                        <div>
                          <p className="text-xs text-slate-600 uppercase tracking-wider">Solution</p>
                          <p className="text-sm text-slate-700">{selectedIdea.proposed_solution}</p>
                        </div>
                      )}
                      {selectedIdea.expected_impact && (
                        <div>
                          <p className="text-xs text-slate-600 uppercase tracking-wider">Impact</p>
                          <p className="text-sm text-slate-700">{selectedIdea.expected_impact}</p>
                        </div>
                      )}
                    </div>

                    {/* AI Actions */}
                    <div className="border-t border-slate-200 pt-4 space-y-2">
                      <Button
                        onClick={() => enrichIdea(selectedIdea.id)}
                        disabled={isEnriching}
                        className="w-full bg-amber-500 hover:bg-amber-400 rounded-none text-white"
                      >
                        {isEnriching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                        {isEnriching ? "Enriching..." : "AI Enrich & Triage"}
                      </Button>
                      <Button
                        onClick={() => evaluateIdea(selectedIdea.id)}
                        disabled={isEvaluating}
                        variant="outline"
                        className="w-full border-amber-500 text-amber-600 hover:bg-amber-50 rounded-none"
                      >
                        {isEvaluating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TrendingUp className="w-4 h-4 mr-2" />}
                        {isEvaluating ? "Evaluating..." : "AI Evaluate & Prioritize"}
                      </Button>
                    </div>

                    {/* AI Enrichment */}
                    {selectedIdea.ai_enrichment && (
                      <div className="bg-amber-50 border border-amber-200 p-3">
                        <p className="text-xs text-amber-600 uppercase tracking-wider mb-2 flex items-center gap-1">
                          <Sparkles className="w-3 h-3" /> AI Enrichment
                        </p>
                        <pre className="text-xs text-slate-700 whitespace-pre-wrap font-['IBM_Plex_Sans']">
                          {selectedIdea.ai_enrichment}
                        </pre>
                      </div>
                    )}

                    {/* AI Evaluation */}
                    {selectedIdea.ai_evaluation && (
                      <div className="bg-purple-50 border border-purple-200 p-3">
                        <p className="text-xs text-purple-600 uppercase tracking-wider mb-2 flex items-center gap-1">
                          <TrendingUp className="w-3 h-3" /> AI Evaluation
                        </p>
                        <pre className="text-xs text-slate-700 whitespace-pre-wrap font-['IBM_Plex_Sans']">
                          {selectedIdea.ai_evaluation}
                        </pre>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-center py-12">
                    <Lightbulb className="w-16 h-16 text-slate-300 mb-4" />
                    <p className="text-slate-600">Select an idea to view details</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Innovation Bot Tab */}
          <TabsContent value="bot" className="mt-0">
            <div className="bg-white border border-slate-200 overflow-hidden" style={{ height: "550px" }}>
              <HermesChat
                contextHint="[Innovation Centre]"
                title="INNOVATION BOT"
                icon={Bot}
                color="from-amber-500 to-orange-600"
                accentColor="text-amber-400"
                layout="tab"
                placeholder="Ask about innovation, submit ideas, get feedback..."
                sessionPrefix="innovation"
              />
            </div>
          </TabsContent>
        </Tabs>
        </div>
        <div className="hidden lg:block w-40 shrink-0 pt-3 border-l border-app pl-3">
          <HubSwitcher />
        </div>
      </main>
    </div>
  );
}
